import React from "react";
import { MinusIcon, PlusIcon } from "lucide-react";
import { activities } from "../../data/activities";
import { outlets } from "../../data/outlets";
import type { BookingType } from "../../types/cafe";
import type { BookingErrors, BookingFormState } from "./useBookingForm";
import { formatIDR } from "../../utils/currency";
interface StepProps {
  form: BookingFormState;
  errors: BookingErrors;
  update: <K extends keyof BookingFormState>(key: K, value: BookingFormState[K]) => void;
}
const types: {
  id: BookingType;
  label: string;
  hint: string;
}[] = [{
  id: "activity",
  label: "Ikut aktivitas",
  hint: "Workshop, gowes, game night"
}, {
  id: "table",
  label: "Reservasi meja",
  hint: "Nongkrong, kerja, atau meeting"
}];
export function BookingDetailsStep({
  form,
  errors,
  update
}: StepProps) {
  const activity = activities.find(a => a.id === form.activityId);
  const availableOutlets = form.type === "activity" && activity ? outlets.filter(o => activity.outletIds.includes(o.id)) : outlets;
  return <div className="space-y-7" data-id="element-71" data-mp="11nz1ep9nktqj">
      <fieldset data-id="element-72" data-mp="16omy9rkkc4so">
        <legend className="text-sm font-semibold text-ink" data-id="element-73" data-mp="16p66uhobpgu1">Apa yang ingin kamu pesan?</legend>
        <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-74" data-mp="sp55z28269ek">
          {types.map(t => {
          const active = form.type === t.id;
          return <button key={t.id} type="button" onClick={() => update("type", t.id)} aria-pressed={active} className={`rounded-card p-4 text-left ring-1 transition-[background-color,box-shadow] duration-150 ${active ? "bg-white ring-2 ring-sage" : "ring-cream-line hover:bg-white/60"}`} data-id="element-75" data-mp="wqqp35b6rjx7">
                <span className="block text-sm font-semibold text-ink" data-id="element-76" data-mp="1fmyohship0kl">{t.label}</span>
                <span className="mt-1 block text-xs text-ink-muted" data-id="element-77" data-mp="23csxu02mo703">{t.hint}</span>
              </button>;
        })}
        </div>
      </fieldset>

      {form.type === "activity" && <div data-id="element-78" data-mp="2hzi72tmolyjo">
          <label htmlFor="activity" className="text-sm font-semibold text-ink" data-id="element-79" data-mp="2hz9ruv336h5b">
            Aktivitas
          </label>
          <select id="activity" value={form.activityId} onChange={e => update("activityId", e.target.value)} aria-invalid={!!errors.activityId} className={`mt-2 w-full rounded-card border bg-white px-4 py-3 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-sage ${errors.activityId ? "border-[#B5543A]" : "border-cream-line"}`} data-id="element-80" data-mp="290eodqrffl5p">
            <option value="" data-id="element-81" data-mp="1qay9fyezvq5r">Pilih aktivitas…</option>
            {activities.map(a => <option key={a.id} value={a.id} data-id="element-82" data-mp="1lcmy1j91x7lf">
                {a.title} — {formatIDR(a.price)}
              </option>)}
          </select>
          {activity && <p className="mt-2 text-xs text-ink-muted" data-id="element-83" data-mp="1bcnrfrw6zrb7">{activity.schedule}</p>}
          {errors.activityId && <p className="mt-2 text-xs text-[#B5543A]" data-id="element-84" data-mp="119qtsunxivbt">{errors.activityId}</p>}
        </div>}

      <fieldset data-id="element-85" data-mp="167so6bqt2h2l">
        <legend className="text-sm font-semibold text-ink" data-id="element-86" data-mp="167itwbyq9nr3">Outlet</legend>
        <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-87" data-mp="1b7oi1i5sgs4f">
          {availableOutlets.map(o => {
          const active = form.outletId === o.id;
          return <button key={o.id} type="button" onClick={() => update("outletId", o.id)} aria-pressed={active} className={`rounded-card px-4 py-3 text-left ring-1 transition-[background-color,box-shadow] duration-150 ${active ? "bg-white ring-2 ring-sage" : "ring-cream-line hover:bg-white/60"}`} data-id="element-88" data-mp="1g4f54pg0gnzc">
                <span className="block text-sm font-semibold text-ink" data-id="element-89" data-mp="3vxvwn1sd5a9s">{o.name}</span>
                <span className="block text-xs text-ink-muted" data-id="element-90" data-mp="4rcppxpne4gr">{o.vibe}</span>
              </button>;
        })}
        </div>
        {errors.outletId && <p className="mt-2 text-xs text-[#B5543A]" data-id="element-91" data-mp="26h1sbb03mcgs">{errors.outletId}</p>}
      </fieldset>

      <div className="flex items-center justify-between" data-id="element-92" data-mp="21iqi2z5w5zy1">
        <div data-id="element-93" data-mp="2g61vl6lmvu0s">
          <p className="text-sm font-semibold text-ink" data-id="element-94" data-mp="2g6ld4m7dggea">
            {form.type === "activity" ? "Peserta" : "Jumlah tamu"}
          </p>
          <p className="text-xs text-ink-muted" data-id="element-95" data-mp="2be0jbkogu3ar">Maksimal 8 orang per pesanan</p>
        </div>
        <div className="flex items-center gap-3" data-id="element-96" data-mp="2p5f49x1xoekg">
          <button type="button" onClick={() => update("guests", Math.max(1, form.guests - 1))} disabled={form.guests <= 1} className="flex h-9 w-9 items-center justify-center rounded-full ring-1 ring-cream-line transition-colors duration-150 hover:bg-white disabled:opacity-40" aria-label="Kurangi" data-id="element-97" data-mp="2l5ohs5jfwklq">
            <MinusIcon className="h-4 w-4" data-id="element-98" data-mp="14t9847zvlrqj" />
          </button>
          <span className="w-6 text-center text-base font-semibold tabular-nums" aria-live="polite" data-id="element-99" data-mp="ztdbwuxf74lj">
            {form.guests}
          </span>
          <button type="button" onClick={() => update("guests", Math.min(8, form.guests + 1))} disabled={form.guests >= 8} className="flex h-9 w-9 items-center justify-center rounded-full ring-1 ring-cream-line transition-colors duration-150 hover:bg-white disabled:opacity-40" aria-label="Tambah" data-id="element-100" data-mp="3bhro7dpm5qqq">
            <PlusIcon className="h-4 w-4" data-id="element-101" data-mp="2sncplp77s03f" />
          </button>
        </div>
      </div>
    </div>;
}